import { Autocomplete, AutocompleteProps, AutocompleteValue, Box, Chip, SxProps, TextField } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';
import deepEqual from 'deep-equal';
import {
  compact,
  filter,
  findIndex,
  flatMap,
  isArray,
  isEmpty,
  isString,
  map,
  size,
  split,
  trim,
} from 'es-toolkit/compat';
import React, { ReactNode, useState } from 'react';

export const autocompleteCellEditorSxOverrides: SxProps = {
  // Remove borders from the input
  '& .MuiAutocomplete-inputRoot fieldset': {
    border: 'none',
  },
  // Stop the input element from starting a new line when empty
  '& .MuiAutocomplete-inputRoot .MuiInputBase-input[value=""]': {
    minWidth: 0,
  },
  // Fill the whole cell height with the input
  '&, & .MuiFormControl-root, & .MuiAutocomplete-inputRoot': {
    height: '100%',
  },
  // Align the tags to the top
  '& .MuiInputBase-root': {
    alignContent: 'flex-start',
    padding: 1,
  },
};

export const sanitizeFreeSoloValues = <T,>(values: T[]): T[] => {
  return flatMap(values, (value) => {
    if (isString(value)) {
      return compact(map(split(value, ','), (item) => trim(item))) as T[];
    }

    return [value];
  });
};
interface AutocompleteEditInputCellProps<
  T,
  Multiple extends boolean = undefined,
  DisableClearable extends boolean = undefined,
  FreeSolo extends boolean = undefined,
  V = T
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> {
  params: GridRenderEditCellParams<any, any, string>;
  fixedOptions?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['options'];
  renderInput?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderInput'];
  getValueFromSelection?: (selection: T) => V;
  maxSelections?: Multiple extends true ? number : undefined;
  autoFocusCell?: boolean;
  hideDropdownIndicator?: boolean;
}

export function AutocompleteEditInputCell<
  T,
  Multiple extends boolean = undefined,
  DisableClearable extends boolean = undefined,
  FreeSolo extends boolean = undefined,
  V = T
>({
  autoFocusCell,
  maxSelections,
  getValueFromSelection,
  hideDropdownIndicator,
  fixedOptions,
  params,
  ...props
}: AutocompleteEditInputCellProps<T, Multiple, DisableClearable, FreeSolo, V>) {
  const { id, field } = params;
  const apiRef = useGridApiContext();
  const [didInteract, setDidInteract] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [, setSelectedValues] = useState<AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>>(
    params.value ?? props.multiple
      ? ([] as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>)
      : ('' as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>)
  );

  const normalizedValue: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['value'] = React.useMemo(
    () => (props.multiple ? (isEmpty(params.value) || !isArray(params.value) ? [] : params.value) : params.value) ?? '',
    [props.multiple, params.value]
  );

  const handleBlur = () => {
    // we need the onBlur function only in cases of multiple and freeSolo,
    // this is because when blurred, the value isn't added to the selectedValues, but also the text remains
    // we want to add the text on blur to the selectedValues and clear the inputValue
    if (!props.multiple || !props.freeSolo) {
      return;
    }

    const trimmedInput = trim(inputValue);
    if (trimmedInput) {
      const newValue = sanitizeFreeSoloValues([...(normalizedValue as T[]), trimmedInput]) as AutocompleteValue<
        T,
        Multiple,
        DisableClearable,
        FreeSolo
      >;
      handleSelectedValuesChange(newValue);
      setInputValue('');
    }
  };

  const handleSelectedValuesChange = async (
    newValue: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
    reason: string = ''
  ) => {
    if (
      props.multiple &&
      reason !== 'clear' &&
      reason !== 'removeOption' &&
      maxSelections &&
      isArray(newValue) &&
      size(newValue) > maxSelections
    ) {
      return;
    }
    const filteredValue = (
      props.multiple
        ? isArray(newValue)
          ? filter(newValue, (v) => 0 > findIndex(fixedOptions, (o) => deepEqual(v, o)))
          : []
        : newValue
    ) as AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['value'];
    const sanitizedValue =
      props.multiple && props.freeSolo && isArray(filteredValue)
        ? sanitizeFreeSoloValues(filteredValue as T[])
        : filteredValue;
    const newNormalizedValue: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> = getValueFromSelection
      ? props.multiple
        ? (map(sanitizedValue as T[], getValueFromSelection) as AutocompleteValue<
            T,
            Multiple,
            DisableClearable,
            FreeSolo
          >)
        : (getValueFromSelection(sanitizedValue as T) as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>)
      : (sanitizedValue as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>);

    setSelectedValues(newNormalizedValue);

    await apiRef.current.setEditCellValue({
      id: id,
      field: field,
      value: newNormalizedValue,
    });
  };

  const handleChange: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['onChange'] = async (
    event,
    newValue,
    reason
  ) => {
    event.stopPropagation();
    await handleSelectedValuesChange(newValue, reason);

    if (!didInteract) {
      setDidInteract(true);
    }
  };

  const getOptionLabel = React.useCallback(
    props.getOptionLabel ??
      ((option: T) => `${typeof option === 'string' ? option : ((option as any)?.label || option) ?? ''}`),
    [props.getOptionLabel]
  );

  return (
    <Autocomplete
      sx={autocompleteCellEditorSxOverrides}
      // Open the popup on focus, but not when a row editing is started (which focuses all the cells, without blur)
      openOnFocus={didInteract}
      renderInput={(inputParams) => (
        <TextField autoFocus={autoFocusCell} error={params.error} onBlur={handleBlur} {...inputParams} />
      )}
      {...(hideDropdownIndicator
        ? {
            componentsProps: {
              // Hide the dropdown indicator from the input
              popupIndicator: { style: { display: 'none' } },
            },
          }
        : {})}
      renderTags={
        fixedOptions
          ? (tagValue, getTagProps) => (
              <Box
                sx={{
                  maxHeight: 150,
                  overflow: 'auto',
                }}
              >
                {map(tagValue, (option, index) => (
                  <Chip
                    key={option}
                    label={getOptionLabel ? getOptionLabel(option) : (option as ReactNode)}
                    {...getTagProps({ index })}
                    disabled={findIndex(fixedOptions, (fixed) => deepEqual(option, fixed)) !== -1}
                  />
                ))}
              </Box>
            )
          : undefined
      }
      fullWidth
      value={normalizedValue}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(e, newInput) => {
        if (!didInteract) {
          setDidInteract(true);
        }
        setInputValue(newInput);
      }}
      onClick={() => setDidInteract(true)}
      options={props.options ?? []}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      {...props}
      limitTags={props.limitTags ?? maxSelections}
    />
  );
}
